// Compact, credential-free error shape — the single server-side definition of
// how an error serializes into logs. Raw HTTP-client errors (axios
// AxiosError, AI SDK AI_APICallError) carry the full request config
// (query-string API keys), headers, request bodies (entire prompts + JSON
// schemas), and complete response bodies — megabytes that end up gzipped into
// Datadog log payloads. This keeps only what's actionable.

const FIELD_CAP_CHARS = 2048
const BODY_PROJECTION_MAX_ENTRIES = 10
const BODY_OMIT_THRESHOLD_BYTES = FIELD_CAP_CHARS * 8

export interface SlimError {
  name?: string
  message?: string
  stack?: string
  code?: string
  status?: number
  url?: string
  bodySnippet?: string
  statusCode?: number
  isRetryable?: boolean
  modelId?: string
  responseBody?: string
  serializationError?: string
}

const cap = (value: unknown): string | undefined =>
  typeof value === 'string' ? value.slice(0, FIELD_CAP_CHARS) : undefined

/** Drop query string AND fragment — both can carry credentials. */
const stripUrl = (url: string): string => {
  try {
    const u = new URL(url)
    return u.origin + u.pathname
  } catch {
    const query = url.indexOf('?')
    const fragment = url.indexOf('#')
    const cut = Math.min(
      query === -1 ? url.length : query,
      fragment === -1 ? url.length : fragment
    )
    return url.slice(0, cut)
  }
}

const bodySnippetFrom = (
  data: unknown,
  contentLength: unknown
): string | undefined => {
  if (data === undefined || data === null) return undefined
  if (typeof data === 'string') return cap(data)
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
    return data.toString('utf8', 0, FIELD_CAP_CHARS)
  }
  if (ArrayBuffer.isView(data) || data instanceof ArrayBuffer) {
    return `[binary body: ${data.byteLength} bytes]`
  }
  if (typeof data !== 'object') return cap(String(data))

  const declaredBytes = Number(contentLength)
  if (
    Number.isFinite(declaredBytes) &&
    declaredBytes > BODY_OMIT_THRESHOLD_BYTES
  ) {
    return `[body omitted: ${declaredBytes} bytes]`
  }

  // Shallow projection — never JSON.stringify the whole body graph.
  const projection: Record<string, unknown> = {}
  let entries = 0
  for (const key in data) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue
    if (entries >= BODY_PROJECTION_MAX_ENTRIES) {
      projection['...'] = '[more entries omitted]'
      break
    }
    entries += 1
    const value = (data as Record<string, unknown>)[key]
    if (value !== null && typeof value === 'object') {
      projection[key] = Array.isArray(value)
        ? `[array of ${value.length}]`
        : '[object]'
    } else if (typeof value === 'string') {
      projection[key] = value.slice(0, 256)
    } else {
      projection[key] = value
    }
  }
  try {
    return cap(JSON.stringify(projection))
  } catch {
    return '[unserializable body]'
  }
}

/**
 * Whether a metadata value is an error that should be slimmed before it
 * ships: a real Error, a duck-typed axios error, or a duck-typed AI SDK
 * error. Errors logged as metadata on non-error levels bypass the
 * errorSerializer (which only covers the withError path), so the logger's
 * guard step uses this to slim them there too.
 */
export const isErrorShaped = (value: unknown): boolean => {
  if (value instanceof Error) return true
  if (value === null || typeof value !== 'object') return false
  try {
    const err = value as Record<string, unknown>
    if (err.isAxiosError === true) return true
    if ('requestBodyValues' in err) return true
    if ('responseBody' in err && 'statusCode' in err) return true
  } catch {
    return false
  }
  return false
}

export const slimError = (error: unknown): SlimError => {
  try {
    if (error === null || error === undefined) return {}
    if (typeof error !== 'object') {
      return typeof error === 'string' ? { message: cap(error) } : {}
    }

    const err = error as Record<string, any>
    const response = err.response as
      | { status?: number; data?: unknown; headers?: Record<string, unknown> }
      | undefined

    const rawUrl = typeof err.url === 'string' ? err.url : err.config?.url
    const bodySnippet =
      typeof err.bodySnippet === 'string'
        ? cap(err.bodySnippet)
        : bodySnippetFrom(response?.data, response?.headers?.['content-length'])

    const slim: SlimError = {
      name: typeof err.name === 'string' ? err.name : undefined,
      message: cap(err.message),
      stack: cap(err.stack),
      code: typeof err.code === 'string' ? err.code : undefined,
      status: err.status ?? response?.status,
      url: typeof rawUrl === 'string' ? stripUrl(rawUrl) : undefined,
      bodySnippet,
    }
    if (err.statusCode !== undefined) slim.statusCode = err.statusCode
    if (err.isRetryable !== undefined) slim.isRetryable = err.isRetryable
    if (typeof err.modelId === 'string') slim.modelId = err.modelId
    if (typeof err.responseBody === 'string') {
      slim.responseBody = cap(err.responseBody)
    }
    return slim
  } catch {
    // Both loglayer's errorSerializer call and pino's serializer call sit
    // outside any try — a throw here would escape into application code.
    const fallback: SlimError = { serializationError: '[slimError failed]' }
    try {
      const name = (error as { name?: unknown } | null)?.name
      if (typeof name === 'string') fallback.name = name
    } catch {
      /* unreadable */
    }
    try {
      const message = (error as { message?: unknown } | null)?.message
      if (message !== undefined) fallback.message = cap(String(message))
    } catch {
      /* unreadable */
    }
    return fallback
  }
}
